import {
  NetworkStatus,
  makeVar,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useReactiveVar,
} from '@apollo/client';
import {
  Checkbox,
  DirectionalHint,
  IColumn,
  IDetailsRowBaseProps,
  IDetailsRowProps,
  IRenderFunction,
  Icon,
  PrimaryButton,
  ProgressIndicator,
  SelectionMode,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { setApprovalPanelReactive } from 'approvals/ApprovalDetails';
import { ApprovalSearchBar } from 'approvals/ApprovalSearchBar';
import {
  ApprovalUserActions,
  ApprovalUserActionsVariables,
} from 'approvals/__generated__/ApprovalUserActions';
import { setApprovalNodes } from 'approvals/ui';
import clsx from 'clsx';
import { AmountColumnTextView } from 'common/components/AmountView/AmountColumnTextView';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { cutToast } from 'utility/toast';
import { FilterArrayType } from 'common/components/Filters';
import {
  HighLightActiveLink,
  HighlightTextView,
} from 'common/components/HighLight';
import { InfiniteList } from 'common/components/InfiniteList';
import {
  MultiSelectOption,
  MultiSelectTags,
} from 'common/components/MultiSelectTags';
import { ColumnData } from 'common/components/SearchBar';
import { TABLE_ROWS } from 'common/constants';
import {
  PayCycleBankingDocumentPackageStatus,
  PayCycleBankingDocumentPackageStatusVariables,
} from 'common/graphql/DocumentPackageSubscription/__generated__/PayCycleBankingDocumentPackageStatus';
import {
  PurchaseOrderDocumentPackageStatus,
  PurchaseOrderDocumentPackageStatusVariables,
} from 'common/graphql/DocumentPackageSubscription/__generated__/PurchaseOrderDocumentPackageStatus';
import {
  TransactionDocumentPackageStatus,
  TransactionDocumentPackageStatusVariables,
} from 'common/graphql/DocumentPackageSubscription/__generated__/TransactionPackageStatus';
import { useCommonStyles } from 'common/styles';
import {
  ApprovalActionTypeInput,
  SearchRequestInput,
  UserApprovalFilter,
} from 'common/types/globalTypes';
import { getColumn, getSortedColumns } from 'common/utils/columnUtilities';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { StringParam } from 'serialize-query-params';
import { useQueryParams } from 'use-query-params';
import { globalMode, setGlobalSearchText } from 'utility/cache/ui';
import { ApprovalInputGroup } from '../ApprovalInputGroup';
import {
  UserApprovalHistorySearchHints,
  UserApprovalHistorySearchHintsVariables,
} from './__generated__/UserApprovalHistorySearchHints';
import {
  UserApprovalSearch,
  UserApprovalSearchVariables,
  UserApprovalSearch_userApprovalSearch_nodes,
} from './__generated__/UserApprovalSearch';
import {
  UserApprovalSearchFilterTotals,
  UserApprovalSearchFilterTotalsVariables,
} from './__generated__/UserApprovalSearchFilterTotals';
import { useColumns } from './column.data';
import { useStyles } from './index.styles';
import {
  getFilterParams,
  toApprovalFilterVariable,
  toOrderByVariable,
} from './utils';

import APPROVAL_USER_ACTIONS from '../ApprovalUserActions.graphql';
import USER_APPROVAL_SEARCH_HINTS from './UserApprovalHistorySearchHints.graphql';
import USER_APPROVAL_SEARCH from './UserApprovalSearch.graphql';

import PAYMENT_DOCUMENT_PACKAGE_STATUS from 'common/graphql/DocumentPackageSubscription/PayCycleBankingDocumentPackageStatus.graphql';
import PO_DOCUMENT_PACKAGE_STATUS from 'common/graphql/DocumentPackageSubscription/PurchaseOrderDocumentPackageStatus.graphql';
import DOCUMENT_PACKAGE_STATUS from 'common/graphql/DocumentPackageSubscription/TransactionPackageStatus.graphql';
export const setApprovalLayoutType = makeVar<string | null | undefined>(
  undefined
);

import USER_APPROVAL_SEARCH_FILTER_TOTALS from './UserApprovalSearchFilterTotals.graphql';

import { HelpIcon } from 'common/trainingSystem/HelpIcon';
import { checkBoxStyle } from 'common/utils/listViewCheckBoxStyles';

export interface UserApprovalItem extends UserApprovalSearch_userApprovalSearch_nodes {
  key: string;
  name: string;
  number: string;
  description: string;
  date: string;
  amount: string;
  currencyData: string;
  role: string;
  status: string;
  departmentName: string;
  businessUnitName: string;
  periodYear: string;
  requesterDate: string;
  approvers: string;
  approvedDate: string;
  amendedByName: string;
  rejectors: string;
  rejectedDate: string;
  amendedDate: string;
  requesterName: string;
  additionalApprovalInformation: string;
  phaseName: string;
}

interface ApprovalsNavProps {
  processDone: boolean;
}
export const Approvals: React.FC = () => {
  const renderRef = useRef(false);
  const { columns } = useColumns();
  const history = useHistory();
  const { state } = useLocation<ApprovalsNavProps | undefined>();
  const globalState = useReactiveVar(globalMode);
  const globalSearchText = useReactiveVar(setGlobalSearchText);
  let approvalList: UserApprovalItem[] = [];
  const [urlParameters, setUrlParameters] = useQueryParams({
    t: StringParam,
  });
  const client = useApolloClient();
  const { t: searchParam } = urlParameters;
  const [searchValue, setsearchValue] = useState<string | undefined | null>(
    searchParam! || null
  );
  const [historyFilter, sethistoryFilter] = useState(false);
  const [selectedHint, setselectedHint] = useState<number | null>();
  const [queuedFilter, setqueuedFilter] = useState(false);
  const [actionPendingFilter, setActionPendingFilter] = useState(!globalState);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [searchFilters, setSearchFilters] = useState<FilterArrayType[]>();
  const [approvalActionList, setapprovalActionList] = useState<
    ApprovalActionTypeInput[]
  >([]);
  const [renderControl, setRenderControl] = useState<boolean>(false);
  const hintFlagRef = useRef<boolean>(true);
  const [gridColumns, setGridColumns] = useState<ColumnData[]>(columns);
  const commonStyles = useCommonStyles();
  const styles = useStyles();
  const [isAllApprovalChecked, setIsAllApprovalChecked] = React.useState(false);

  const isApprovalPanelVisible = useReactiveVar(setApprovalPanelReactive);

  const [
    fetchApproval,
    {
      loading: userApprovalListLoading,
      data: userApprovalsList,
      error: userApprovalsListError,
      variables,
      networkStatus,
      fetchMore,
    },
  ] = useLazyQuery<UserApprovalSearch, UserApprovalSearchVariables>(
    USER_APPROVAL_SEARCH,
    {
      variables: {
        first: TABLE_ROWS,
        isQueuedSearch: false,
        isHistorySearch: false,
        searchRequest: {
          searchText: [''],
          searchHintId: null,
          isMatchAny: true,
        },
        filter: { iconType: { isApprovalActionAvailable: { equalTo: true } } },
        orderBy: ['URGENCY_LEVEL_ASC', 'PRIMARY_KEY_ASC', 'AMOUNT_1_ASC'],
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const [fetchTotals, { data: totalsData, variables: totalsVariable }] =
    useLazyQuery<
      UserApprovalSearchFilterTotals,
      UserApprovalSearchFilterTotalsVariables
    >(USER_APPROVAL_SEARCH_FILTER_TOTALS, {
      variables: {
        isQueuedSearch: false,
        isHistorySearch: false,
        searchRequest: {
          searchText: [''],
          searchHintId: null,
          isMatchAny: true,
        },
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    });

  useEffect(() => {
    if (state?.processDone) {
      setapprovalActionList([]);
      fetchTotals({ variables: totalsVariable });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTotals, state]);

  const [processApproval, { loading: processApprovalLoading }] = useMutation<
    ApprovalUserActions,
    ApprovalUserActionsVariables
  >(APPROVAL_USER_ACTIONS, { errorPolicy: 'all' });
  const [fetchUserApprovalHints, { data: userApprovalHintsData }] =
    useLazyQuery<
      UserApprovalHistorySearchHints,
      UserApprovalHistorySearchHintsVariables
    >(USER_APPROVAL_SEARCH_HINTS, {
      variables: {
        searchRequest: { searchText: [''], searchHintId: null },
        isHistorySearch: false,
        isQueuedSearch: true,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    });

  useEffect(() => {
    const filteredNodes = userApprovalsList?.userApprovalSearch?.nodes.filter(
      (item) => item?.iconType?.isApprovalActionAvailable
    );
    setApprovalNodes(filteredNodes);
  }, [userApprovalsList]);

  const refetching =
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables;
  // TRANSFORMING THE DATA FOR UI BINDING WITH LIST
  const transformedData = refetching
    ? undefined
    : userApprovalsList?.userApprovalSearch?.nodes.map(
        (appr) =>
          ({
            ...appr,
            ...appr.userApprover,
            key: appr.id,
            name: appr.description1,
            number: appr.description3,
            description: appr.description2,
            date: appr.date1 ? appr.date1 : '',
            amount: appr.amount1?.toString(),
            currencyData: appr.currency?.isoCode,
            role: appr.approvalPersona,
            status: appr.iconType?.iconType,
            departmentName: appr.department?.name,
            businessUnitName: appr.businessUnit?.name,
            periodYear: appr.companyCorporatePeriod?._periodYear,
            additionalApprovalInformation: appr.additionalApprovalInformation,
            phaseName: appr.userApprovalPhaseType?.phaseName,
          }) as UserApprovalItem
      );
  if (transformedData) approvalList = transformedData;

  useEffect(() => {}, [approvalList]);

  const _renderItemColumn = (
    item: UserApprovalItem | undefined,
    _index: number | undefined,
    column: IColumn | undefined
  ) => {
    if (item) {
      const fieldContent = item?.[
        column?.fieldName as keyof UserApprovalItem
      ] as string;
      let approverData;
      let selectedApproval = approvalActionList.filter((id) => {
        return id.entityApprovalBranchId === item.entityApprovalBranchId;
      });
      const approvalData = selectedApproval[0];
      let rejectorsList: string = '';
      if (item?.userApprover?.approvers)
        approverData = item?.userApprover?.approvers.split(',').join(', ');
      if (item?.userApprover?.rejectors)
        rejectorsList = item?.userApprover?.rejectors.split(',').join(', ');
      const { _isUrgentApproval } = { ...item };
      const Content: React.FC = () => {
        return (
          <Stack className={styles.container}>
            <Stack className={styles.urgentHeader}>
              <Text className={styles.title}>Urgent</Text>
            </Stack>
            <Stack tokens={{ padding: 20 }}>
              <Text>{item?.approvalTopic}</Text>
            </Stack>
          </Stack>
        );
      };
      switch (column?.key) {
        case 'urgencyLevel':
          return (
            <TooltipHost className={styles.containerMain} content={<Content />}>
              <Stack
                tokens={{ padding: '0px 0px 0px 10px' }}
                verticalAlign="center"
              >
                {_isUrgentApproval && (
                  <Icon
                    iconName="FabricFolderFill"
                    className={commonStyles.urgentBox}
                  />
                )}
              </Stack>
            </TooltipHost>
          );
        case 'name':
          return (
            <Stack
              tokens={{ childrenGap: 10 }}
              verticalAlign="center"
              horizontal
              className={styles.onrenderColumnStack}
            >
              {isApprovalPanelVisible && (
                <ApprovalInputGroup
                  selectedApproval={approvalData}
                  approvalData={item}
                  onChange={onActionChange}
                  hideActionButtons
                />
              )}
              <HighLightActiveLink
                key={item?.key}
                highlightText={globalSearchText}
                text={item?.name}
                to={`/approvals/approval/${item?.key}`}
                preventDefault
                onLinkClick={() => {
                  setApprovalLayoutType(item?.layoutType);
                  history.push(`/approvals/approval/${item?.key}`);
                }}
              />
            </Stack>
          );
        case 'status':
          return (
            <Stack
              verticalAlign="center"
              tokens={{ padding: '0px 0px 0px 20px' }}
              className={styles.onrenderColumnStack}
            >
              <ApprovalInputGroup
                selectedApproval={approvalData}
                approvalData={item}
                onChange={onActionChange}
              />
            </Stack>
          );
        case 'date':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                className={styles.contentColumnAlignRight}
                highlightText={globalSearchText}
                text={
                  fieldContent === null || !fieldContent
                    ? ''
                    : dateFormat(dateConvertions(fieldContent))
                }
              />
            </Stack>
          );
        case 'amount':
          return (
            <AmountColumnTextView
              value={fieldContent}
              searchText={globalSearchText}
            />
          );
        case 'requesterDate':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={
                  item?.userApprover?.requesterDate! &&
                  getGlobalDateFormat(item.userApprover?.requesterDate!)
                }
              />
            </Stack>
          );
        case 'approvedDate':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={
                  item?.userApprover?.approvedDate! &&
                  getGlobalDateFormat(item.userApprover?.approvedDate!)
                }
              />
            </Stack>
          );
        case 'rejectedDate':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={
                  item?.userApprover?.rejectedDate! &&
                  getGlobalDateFormat(item.userApprover?.rejectedDate!)
                }
              />
            </Stack>
          );
        case 'amendedDate':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={
                  item?.userApprover?.amendedDate! &&
                  getGlobalDateFormat(item.userApprover?.amendedDate!)
                }
              />
            </Stack>
          );
        case 'approvers':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={approverData ? approverData : ''}
              />
            </Stack>
          );
        case 'rejectors':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <HighlightTextView
                highlightText={globalSearchText}
                text={rejectorsList}
              />
            </Stack>
          );
        default:
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              {fieldContent && (
                <HighlightTextView
                  highlightText={globalSearchText}
                  text={
                    fieldContent === null || !fieldContent ? '' : fieldContent
                  }
                />
              )}
            </Stack>
          );
      }
    }
  };

  const _onRenderRow: IRenderFunction<IDetailsRowProps> = (
    props,
    defaultRender
  ) => {
    if (!props) {
      return null;
    }
    const { item } = { ...props };
    const rowItem = item as UserApprovalItem;
    const { _isUrgentApproval } = { ...rowItem };
    const newProps: IDetailsRowProps | undefined = props
      ? {
          ...props,
          className: clsx({
            [styles.rowBaseStyle]: true,
            [commonStyles.urgentRow]: !!_isUrgentApproval,
          }),
        }
      : undefined;
    const tootTipMessage = 'View to approve.';
    const tooltipVisible =
      !item.isRequester &&
      !(
        item.iconType?.isApprovalActionAvailable &&
        item._isUserEnMasseApprovalAllowed &&
        !item._isValidating
      );
    const toolTipContent = tooltipVisible ? tootTipMessage : '';

    return (
      <>
        <TooltipHost
          content={toolTipContent}
          directionalHint={DirectionalHint.topLeftEdge}
          tooltipProps={{
            styles: { root: { marginLeft: 100 } },
          }}
        >
          {defaultRender?.(newProps)}
        </TooltipHost>
      </>
    );
  };

  const handleSearch = (showMore: boolean, newHints: boolean) =>
    handleSeachGlobal(showMore, newHints, globalState);

  // HANDLE SEARCH QUERY
  const handleSeachGlobal = (
    showMore: boolean,
    newHints: boolean,
    globalValue: boolean
  ) => {
    if (!showMore) {
      setapprovalActionList([]);
    }
    setIsAllApprovalChecked(false);
    const searchRequest: SearchRequestInput = {
      searchText: [searchValue ? searchValue : ''],
      searchHintId: selectedHint,
      isMatchAny: true,
    };
    const searchHintsRequest: SearchRequestInput = {
      searchText: [searchValue ? searchValue : ''],
    };
    const variables: UserApprovalSearchVariables = {
      first: TABLE_ROWS,
      isHistorySearch: historyFilter,
      isQueuedSearch: queuedFilter,
      searchRequest,
      after: showMore
        ? userApprovalsList?.userApprovalSearch?.pageInfo.endCursor
        : undefined,
      filter: searchFilters?.length
        ? ({
            and: [
              ...toApprovalFilterVariable(searchFilters),
              {
                iconType: {
                  isApprovalActionAvailable: {
                    equalTo: actionPendingFilter,
                  },
                },
              },
            ],
            or: actionPendingFilter
              ? [
                  {
                    iconType: {
                      isApprovalActionAvailable: {
                        equalTo: actionPendingFilter,
                      },
                    },
                  },
                  {
                    approvalPersona: {
                      equalTo: 'REQUESTER',
                    },
                  },
                  {
                    approvalPersona: {
                      equalTo: 'REQUESTER_APPROVER',
                    },
                  },
                ]
              : undefined,
          } as UserApprovalFilter)
        : globalState
          ? undefined
          : {
              or: actionPendingFilter
                ? [
                    {
                      iconType: {
                        isApprovalActionAvailable: {
                          equalTo: actionPendingFilter,
                        },
                      },
                    },
                    {
                      approvalPersona: {
                        equalTo: 'REQUESTER',
                      },
                    },
                    {
                      approvalPersona: {
                        equalTo: 'REQUESTER_APPROVER',
                      },
                    },
                  ]
                : [],
            },
      orderBy: toOrderByVariable(sortOrderParam!, historyFilter),
      isLinkSearch: globalValue,
    };
    const variableHints: UserApprovalHistorySearchHintsVariables = {
      searchRequest: searchHintsRequest,
      isHistorySearch: historyFilter,
      isQueuedSearch: queuedFilter,
      isLinkSearch: globalValue,
    };
    const filterParams = getFilterParams(searchFilters || []);
    const totalFilterVariables: UserApprovalSearchFilterTotalsVariables = {
      isLinkSearch: globalValue,
      isQueuedSearch: queuedFilter,
      searchRequest,
      isHistorySearch: historyFilter,
      ...filterParams,
    };
    const searchQueryParam =
      searchValue && searchValue.length > 0 ? searchValue : undefined;
    setUrlParameters({ t: searchQueryParam }, 'replace');

    if (showMore) fetchMore?.({ variables });
    else {
      fetchApproval({ variables });
      fetchTotals({ variables: totalFilterVariables });
    }
    if (newHints) fetchUserApprovalHints({ variables: variableHints });
  };

  const approveAll = (isChecked: boolean) => {
    setIsAllApprovalChecked(!isAllApprovalChecked);
    if (isChecked) {
      let approvalData: ApprovalActionTypeInput[] = [];
      approvalList.filter((item) => {
        const isApprovable =
          item.iconType?.isApprovalActionAvailable &&
          item._isUserEnMasseApprovalAllowed &&
          !item._isValidating;
        if (isApprovable) {
          approvalData.push({
            entityApprovalBranchId: item?.entityApprovalBranchId!,
            entityApprovalBranchRowTimestamp:
              item?.entityApprovalBranchRowTimestamp!,
            isDecline: false,
            isApproval: true,
            comments: null,
            isApproveBypassLowerLevels: null,
          });
        }
        return approvalData;
      });
      setapprovalActionList(approvalData);
    } else {
      setapprovalActionList([]);
    }
  };

  const updateAllApprovalSelections = (
    approvalSelectedData: ApprovalActionTypeInput[] | undefined
  ) => {
    const intialApprovalData = approvalList.filter((item) => {
      return item.iconType?.isApprovalActionAvailable;
    });
    const updatedApprovalData = approvalSelectedData?.filter((item) => {
      return item.isApproval;
    });
    if (intialApprovalData.length === updatedApprovalData?.length)
      setIsAllApprovalChecked(true);
    else setIsAllApprovalChecked(false);
  };

  const onActionChange = (
    inputData: ApprovalActionTypeInput | undefined,
    remove: boolean
  ) => {
    if (inputData) {
      let newApprovalActionList = [...approvalActionList];
      const position = newApprovalActionList.findIndex(
        (item) =>
          item.entityApprovalBranchId === inputData.entityApprovalBranchId
      );

      if (position === -1) {
        newApprovalActionList.push(inputData);
      } else {
        if (remove) newApprovalActionList.splice(position, 1);
        else newApprovalActionList[position] = inputData;
      }
      setapprovalActionList(newApprovalActionList);
      updateAllApprovalSelections(newApprovalActionList);
    }
  };

  const onProcessApproval = async () => {
    const { errors, data: responseAfterApproval } = await processApproval({
      variables: {
        input: {
          approveDeclineActions: approvalActionList,
        },
      },

      update: (cache, { data }) => {
        const cacheData = cache.readQuery<
          UserApprovalSearch,
          UserApprovalSearchVariables
        >({
          query: USER_APPROVAL_SEARCH,
          variables: variables,
        });
        if (
          data?.approvalUserActions?.approvalActionResults &&
          data?.approvalUserActions?.approvalActionResults?.length > 0
        ) {
          const mutationResponseArray =
            data.approvalUserActions.approvalActionResults.map(
              (ele) => ele?.userApproval
            );
          const mergedArray = cacheData?.userApprovalSearch?.nodes.map(
            (obj) =>
              mutationResponseArray.find((item) => item?.id === obj.id) || obj
          );
          const updatedData = {
            ...cacheData!,
            userApprovalSearch: {
              ...cacheData?.userApprovalSearch!,
              nodes: mergedArray,
              pageInfo: {
                ...cacheData?.userApprovalSearch?.pageInfo!,
              },
            },
          };

          cache.writeQuery({
            query: USER_APPROVAL_SEARCH,
            data: updatedData,
            variables: variables,
          });
        }
      },
    });
    if (errors?.length) {
      cutToast.error(errors[0].message);
    } else {
      fetchTotals({ variables: totalsVariable });
      setapprovalActionList([]);
      cutToast.success('Your approvals have been processed');
      responseAfterApproval?.approvalUserActions?.approvalActionResults?.forEach(
        (item, index) => {
          const { isApproved, approvalType, entityId } = {
            ...item?.userApproval,
          };

          if (isApproved && entityId) {
            if (approvalType?._isSigningStandalone) {
              const observer = client.subscribe<
                TransactionDocumentPackageStatus,
                TransactionDocumentPackageStatusVariables
              >({
                query: DOCUMENT_PACKAGE_STATUS,
                variables: {
                  id: entityId,
                },
              });

              const subscription = observer.subscribe(({ data, errors }) => {
                if (errors)
                  cutToast.error(
                    'Errors received while Subscribing to document package'
                  );
                else {
                  const { document, status } = {
                    ...data?.transactionDocumentPackageStatus,
                  };
                  if (status === 'REGENERATION_FAILURE') {
                    cutToast.error(
                      'Report generation failed. Document package was not created'
                    );
                  }
                  if (status === 'FAILURE') {
                    cutToast.error('Error while creating Document package ');
                  }
                  if (document) {
                    cutToast.success('Document package created');
                    client.cache.modify({
                      id: client.cache.identify({
                        ...item?.userApproval,
                      }),
                      fields: {
                        _documentPackageId: () => {
                          return document.id;
                        },
                      },
                    });
                  }
                }
                subscription.unsubscribe();
              });
            } else if (approvalType?._isPurchaseOrderSystem) {
              const observer = client.subscribe<
                PurchaseOrderDocumentPackageStatus,
                PurchaseOrderDocumentPackageStatusVariables
              >({
                query: PO_DOCUMENT_PACKAGE_STATUS,
                variables: {
                  id: entityId,
                },
              });

              const subscription = observer.subscribe(({ data, errors }) => {
                if (errors)
                  cutToast.error(
                    'Errors received while Subscribing to document package'
                  );
                else {
                  const { document, status } = {
                    ...data?.purchaseOrderDocumentPackageStatus,
                  };
                  if (status === 'REGENERATION_FAILURE') {
                    cutToast.error(
                      'Report generation failed. Document package was not created'
                    );
                  }
                  if (status === 'FAILURE') {
                    cutToast.error('Error while creating Document package ');
                  }
                  if (document) {
                    cutToast.success('Document package created');
                    client.cache.modify({
                      id: client.cache.identify({
                        ...item?.userApproval,
                      }),
                      fields: {
                        _documentPackageId: () => {
                          return document.id;
                        },
                      },
                    });
                  }
                }
                subscription.unsubscribe();
              });
            } else if (approvalType?._isPaymentTrackingStandalone) {
              item?.userApproval?.approvalHistoryItemsByDisplayEntityApprovalId.nodes.forEach(
                (item, index) => {
                  const observer = client.subscribe<
                    PayCycleBankingDocumentPackageStatus,
                    PayCycleBankingDocumentPackageStatusVariables
                  >({
                    query: PAYMENT_DOCUMENT_PACKAGE_STATUS,
                    variables: {
                      id: item.entityItemId!,
                    },
                  });

                  const subscription = observer.subscribe(
                    ({ data, errors }) => {
                      if (errors)
                        cutToast.error(
                          'Errors received while Subscribing to document package'
                        );
                      else {
                        const { status } = {
                          ...data?.payCycleBankingDocumentPackageStatus,
                        };
                        if (status === 'REGENERATION_FAILURE') {
                          cutToast.error(
                            'Report generation failed. Document package was not created'
                          );
                        }
                        if (status === 'FAILURE') {
                          cutToast.error(
                            'Error while creating Document package '
                          );
                        }
                      }
                      subscription.unsubscribe();
                    }
                  );
                }
              );
            } else {
            }
          }
        }
      );
    }
  };
  const _onColumnClick = (clickedColumn: ColumnData) => {
    const { newColumns, desc } = getSortedColumns(clickedColumn, gridColumns);
    setGridColumns(newColumns);
    setSortOrderParam({
      column: clickedColumn.key,
      direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
    });
  };
  const getNewColumns = () => {
    if (sortOrderParam?.column !== '' && sortOrderParam) {
      const columnData = getColumn(sortOrderParam?.column!, gridColumns);
      const sortedColumns = getSortedColumns(columnData, gridColumns);
      return sortedColumns;
    } else return undefined;
  };
  const getNewColumnsMemo = useCallback(getNewColumns, []);

  useEffect(() => {
    const sortedColumns = getNewColumnsMemo();
    if (sortedColumns && renderRef.current)
      setGridColumns(sortedColumns.newColumns);
  }, [getNewColumnsMemo]);
  const handleSearchWithNewHintsMemo = useCallback(handleSearch, [
    searchValue,
    queuedFilter,
    historyFilter,
    searchFilters,
    actionPendingFilter,
  ]);
  const handleSearchMemo = useCallback(handleSearch, [
    sortOrderParam,
    selectedHint,
  ]);
  useEffect(() => {
    if (renderRef.current === true) {
      handleSearchMemo(false, false);
    }
  }, [selectedHint, sortOrderParam, handleSearchMemo]);
  useEffect(() => {
    handleSearchWithNewHintsMemo(false, true);
    renderRef.current = true;
  }, [
    searchValue,
    queuedFilter,
    historyFilter,
    searchFilters,
    handleSearchWithNewHintsMemo,
  ]);
  const searchHintsOptions: MultiSelectOption[] =
    userApprovalHintsData?.userApprovalSearchHints?.nodes
      ?.slice()
      .sort((a, b) => (a.caption ?? '').localeCompare(b.caption ?? ''))
      .map(
        (hint) =>
          ({
            key: hint.id?.toString(),
            text: `${hint.caption ?? ''} (${hint.resultsCount})`,
          }) as MultiSelectOption
      ) || [];

  const _renderDetailsFooterItemColumn: IDetailsRowBaseProps['onRenderItemColumn'] =
    (_item, _index, column) => {
      if (column?.key === 'amount') {
        return (
          <AmountTextView
            variant="medium"
            className={clsx(styles.amountStack, styles.contentColumnAlignRight)}
            value={totalsData?.userApprovalSearchFilterTotals?.totalAmount1!}
          />
        );
      }

      return '';
    };

  const showFooter =
    !refetching &&
    parseFloat(totalsData?.userApprovalSearchFilterTotals?.totalAmount1!) > 0;

  const massApprovalRows = approvalList.filter((item) => {
    return (
      item.iconType?.isApprovalActionAvailable &&
      item._isUserEnMasseApprovalAllowed &&
      !item._isValidating
    );
  });

  const requesterRows = approvalList.filter((item) => item.isRequester);

  const isShowApproval = !!massApprovalRows.length
    ? approvalList.length === massApprovalRows.length + requesterRows.length
    : false;

  useEffect(() => {
    if (approvalList.length && hintFlagRef.current) {
      setRenderControl(true);
      hintFlagRef.current = false;
    }
  }, [approvalList]);

  return (
    <>
      <Stack
        tokens={{ childrenGap: 20 }}
        className={commonStyles.listHeaderContainer}
      >
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          className={commonStyles.listTitleContainer}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 7 }}>
            <Text variant="xLarge">My Approvals</Text>
            <HelpIcon appLinkType="APPROVALS" />
          </Stack>
          <PrimaryButton
            disabled={
              approvalActionList.length === 0 ||
              !approvalActionList ||
              processApprovalLoading
            }
            text="Process approval"
            onClick={onProcessApproval}
          />
        </Stack>
        <Stack>
          <ApprovalSearchBar
            urlParams={{
              searchParam: searchParam!,
            }}
            columns={gridColumns}
            hintsAvailable={false}
            onQueuedToogle={(value) => {
              if (renderRef.current) globalMode(false);
              setqueuedFilter(value);
            }}
            onFilterChange={setSearchFilters}
            onHistoryToogle={(value) => {
              if (renderRef.current) globalMode(false);
              sethistoryFilter(value);
            }}
            onActionPendingToggle={(value) => {
              if (renderRef.current) globalMode(false);
              setActionPendingFilter(value);
            }}
            actionPendingFilter={actionPendingFilter}
            onRefresh={() => handleSearch(false, true)}
            onEnterPress={(value, globalSearch) => {
              setsearchValue(value);
              setGlobalSearchText(value);
            }}
            onToggleVisibility={setGridColumns}
            onGlobalToggle={(value) => handleSeachGlobal(false, true, value)}
          />
          {processApprovalLoading && <ProgressIndicator />}
          {renderControl && (
            <Stack
              horizontal
              verticalAlign={'center'}
              horizontalAlign={'space-between'}
            >
              {searchHintsOptions.length > 0 && (
                <MultiSelectTags
                  multiSelect={false}
                  options={searchHintsOptions}
                  onSelectionChange={(selctedTags) => {
                    const hints = selctedTags.map((item) => parseInt(item.key));
                    setselectedHint(hints[0]);
                  }}
                />
              )}
              {isShowApproval && (
                <Checkbox
                  boxSide="end"
                  styles={checkBoxStyle}
                  checked={isAllApprovalChecked}
                  label="Approve All"
                  onChange={(_, value) => {
                    approveAll(value!);
                  }}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
      <InfiniteList
        items={approvalList}
        loading={
          (!userApprovalsList && !userApprovalsListError) ||
          userApprovalListLoading
        }
        hasNextPage={
          userApprovalsList?.userApprovalSearch?.pageInfo.hasNextPage
        }
        totalRowsCount={totalsData?.userApprovalSearchFilterTotals?.totalCount1}
        showFooter={showFooter}
        selectionMode={SelectionMode.none}
        columns={gridColumns.filter((_column) => _column.isVisible)}
        onRenderItemColumn={_renderItemColumn}
        onRenderFooterItemColumn={_renderDetailsFooterItemColumn}
        onColumnHeaderClick={(_, column) => {
          if (column) _onColumnClick(column);
        }}
        onLoadMore={() => handleSearch(true, false)}
        onRenderRow={_onRenderRow}
      />
    </>
  );
};
